import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Divider, Grid } from '@mui/material';
import { ApiCustomField, ApiItemTypes, type ApiContract, type ApiItemRef } from '@agunity/api-v4';

import ContentModal from './ContentModal';
import ContractTable from './ContractTable';
import { AgColor } from '../../stories/Styles/AgColor';
import AgLoadingCard from '../../stories/AgLoadingCard';
import AgButton from '../../stories/Components/AgButton';
import AgTypography from '../../stories/Styles/AgTypography';

import { useToast } from '../../redux/hooks/toast';
import { useAppDispatch } from '../../redux/hooks';
import { deleteContractItem, getContractItems, getContractTypes } from './redux';
import { useModal } from '../../redux/hooks/modal';
import type { DropdownOptions } from './types';
import { getCustomFieldsList } from '../../pages/CustomFields/redux';
import * as S from './styled';

type ContractForm = {
  canEdit?: boolean;
  itemRef: ApiItemRef[];
  noPadding?: boolean;
}

const ContractForm = ({ canEdit, itemRef, noPadding }: ContractForm) => {
  const messages = useIntl().formatMessage;
  const { openModal, closeModal } = useModal();
  const { toast } = useToast();

  const dispatch = useAppDispatch();

  const [contracts, setContracts] = useState<ApiContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<Record<string, DropdownOptions[]>>({ contractTypes: [] });
  const [tagList, setTagList] = useState<ApiCustomField[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedItemRef = useMemo(() => itemRef, [JSON.stringify(itemRef)]);

  // Fetch all contract types
  const fetchOtherAPI = useCallback(async () => {
    try {
      const { data: contractTypes } = await dispatch(getContractTypes());
      const itemTypeIds = memoizedItemRef.map(item => item.itemType);
      const filteredContractTypes = contractTypes.filter(type => itemTypeIds.includes(type.itemType));
      const { data: customFieldsList } = await dispatch(getCustomFieldsList({ itemTypes: [ApiItemTypes.Contract] }));

      setOptions({ contractTypes: filteredContractTypes as DropdownOptions[] });
      setTagList(customFieldsList || []);
    } catch (error) {
      console.error('Failed to fetch other options:', error);
    }
  }, [dispatch, memoizedItemRef]);

  // Get all contract items
  const getContracts = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await dispatch(getContractItems(memoizedItemRef));
      setContracts(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, memoizedItemRef]);

  // Contract modal form
  const showConsentForm = useCallback(async (contractId?: number) => {
    openModal({
      closeIcon: true,
      autoClose: false,
      maxHeight: 'calc(100vh - 100px)',
      title: messages({ id: !canEdit ? 'CONTRACTS_VIEW_MODAL_TITLE' : (contractId ? 'CONTRACTS_EDIT_MODAL_TITLE' : 'CONTRACTS_ADD_NEW_MODAL_TITLE') }),
      body: (
        <ContentModal
          canEdit={canEdit}
          itemRef={memoizedItemRef}
          options={options}
          closeModal={closeModal}
          isAdd={!contractId}
          contractId={contractId!}
          callback={getContracts}
          tagList={tagList}
        />
      )
    });
  }, [canEdit, closeModal, getContracts, memoizedItemRef, messages, openModal, options, tagList]);

  const handleDelete = useCallback(async (id: number) => {
    try {
      await dispatch(deleteContractItem(id));
      toast('success', messages({ id: 'CONTRACTS_DELETE_SUCCESS' }), { temporary: true });
      closeModal();
    } catch (error) {
      console.error(error);
    } finally {
      getContracts();
    }
  }, [closeModal, dispatch, getContracts, messages, toast]);

  const showDeleteModal = useCallback(async (id: number) => {
    openModal({
      closeIcon: true,
      autoClose: false,
      state: messages({ id: 'WARNING' }),
      title: messages({ id: 'CONTRACTS_DELETE_MODAL_MESSAGE1' }),
      body: (
        <AgTypography sx={{ minHeight: 150 }}>
          {messages({ id: 'CONTRACTS_DELETE_MODAL_MESSAGE2' })}
        </AgTypography>
      ),
      maxHeight: 500,
      actions: [
        { variant: 'outlined', label: messages({ id: 'CANCEL' }), style: { marginRight: '32px' } },
        { label: messages({ id: 'DELETE' }), onClick: () => handleDelete(id) }
      ]
    });
  }, [handleDelete, messages, openModal]);

  useEffect(() => {
    const debounce = setTimeout(() => getContracts());
    return () => clearTimeout(debounce);
  }, [getContracts]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchOtherAPI());
    return () => clearTimeout(debounce);
  }, [fetchOtherAPI]);

  return (
    <S.Main noPadding={noPadding}>
      <Grid container gap={2}>
        <Grid item xs>
          <AgTypography
            contentStyle='AP.H5.UPPERCASE.Bold'
            message={messages({ id: 'CONTRACTS' })}
            style={{ color: AgColor.grey6 }}
          />

          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            message={messages({ id: 'CONTRACTS_DESCRIPTION' })}
            style={{ marginTop: '8px', color: AgColor.grey7 }}
          />
        </Grid>

      {canEdit ? (
        <Grid item>
          <AgButton
            small={!noPadding}
            color='secondary'
            variant='outlined'
            label={messages({ id: 'CONTRACTS_ADD_BTN' })}
            onClick={() => showConsentForm()}
            disabled={(options?.contractTypes || []).length === 0}
            style={{
              marginTop: '8px',
              height: noPadding ? 'unset' : 24,
              fontSize: noPadding ? 12 :10,
              whiteSpace: 'nowrap'
            }}
          />
        </Grid>
      ) : null}

        <Grid item xs={12}>
          {isLoading
            ? (
              <AgLoadingCard
                text={messages({ id: 'LOADING' })}
                style={{ position: 'relative', height: '60vh' }}
              />
            )
            : contracts.length === 0 ? (
              <AgTypography contentStyle={'AP.Paragraph.Small.SemiBold'} style={{ color: AgColor.grey7 }} >
                {messages({ id: 'CONTRACTS_NO_CONTRACTS' })}
              </AgTypography>
            ) : (
                <ContractTable
                  canEdit={canEdit}
                  options={options}
                  contracts={contracts}
                  onEdit={({ id }) => showConsentForm(id)}
                  onDelete={({ id }) => showDeleteModal(id!)}
                />
              )
          }
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ margin: '16px 0', color: AgColor.grey4 }} />
        </Grid>

      </Grid>
    </S.Main>
  );
};

export default React.memo(ContractForm);